import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormControl } from "@/components/ui/form";
import type { Fund } from "@/hooks/useFunds";
import { pickableFunds } from "@/lib/pickableFunds";
import { useTranslation } from "@/hooks/useTranslation";
import { formatFundLabel } from "./fund-label";

/**
 * A non-fund choice rendered above the fund list — e.g. "No fund" on a
 * document header, or "Inherit — {fund}" / "No fund" on a per-line override.
 * The caller owns the sentinel `value` and its meaning; `FundSelect` only
 * renders it and hands it back through `onValueChange`.
 */
export interface FundSelectSpecialOption {
  value: string;
  label: string;
}

interface FundSelectProps {
  /**
   * The org's funds, already fetched by the caller (`useFunds`) — pass them
   * all; the picker narrows them itself.
   *
   * This is a POSTING picker, so it offers only awarded funds
   * (`is_active`, generated as `status = 'awarded'`) plus the one already
   * selected (`value`) — the app-wide `pickableFunds` rule shared with every
   * posting form's fund select. A pipeline, not-awarded or closed fund can't
   * be chosen for new money, but a saved one never falls off the trigger on
   * edit. Planning surfaces (budgets, report filters) list every fund and do
   * not use this picker.
   */
  funds: Fund[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Applied to the trigger — e.g. `border-destructive` for a field error. */
  className?: string;
  /** Rendered above the fund items, in the order given. */
  specialOptions?: FundSelectSpecialOption[];
  disabled?: boolean;
  /**
   * Id for the trigger, so a plain `<Label htmlFor>` outside a react-hook-form
   * `FormItem` names it. Ignored with `formControl` (FormControl owns the id).
   */
  id?: string;
  "aria-label"?: string;
  /**
   * Inside a react-hook-form `FormItem`: wraps the trigger in `FormControl`,
   * so the `FormLabel` targets it (`htmlFor` → trigger id) and it carries
   * `aria-invalid` / `aria-describedby` — the shadcn `<FormControl><SelectTrigger>`
   * wiring. Leave it off outside a `FormField` (FormControl needs its context).
   */
  formControl?: boolean;
}

/**
 * Presentational fund picker — every posting form's fund select. No data
 * fetching; the caller passes `funds` and owns the value mapping (including
 * any sentinel it needs for "inherit" or "explicitly no fund"). Opt into the
 * react-hook-form label/ARIA wiring with `formControl`.
 */
export function FundSelect({
  funds,
  value,
  onValueChange,
  placeholder,
  className,
  specialOptions,
  disabled = false,
  id,
  "aria-label": ariaLabel,
  formControl = false,
}: FundSelectProps) {
  const { t } = useTranslation();
  const trigger = (
    <SelectTrigger
      // Only when set and not under FormControl: a child `id` (even undefined)
      // would override the one FormControl's Slot hands the trigger.
      {...(id && !formControl ? { id } : {})}
      className={cn("w-full", className)}
      aria-label={ariaLabel}
    >
      <SelectValue placeholder={placeholder ?? t("billing.form.selectFund")} />
    </SelectTrigger>
  );
  return (
    <Select value={value ?? undefined} onValueChange={onValueChange} disabled={disabled}>
      {formControl ? <FormControl>{trigger}</FormControl> : trigger}
      <SelectContent>
        {specialOptions?.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {pickableFunds(funds, value).map((fund) => (
          <SelectItem key={fund.id} value={fund.id}>
            {formatFundLabel(fund)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
